import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  VolumeUp,
  VolumeOff,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  Home as HomeIcon,
  Flag as FlagIcon,
  Bolt as BoltIcon,
  Diamond as DiamondIcon,
  EmojiEvents as EmojiEventsIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  WbSunny as WbSunnyIcon,
  Park as ParkIcon,
  AutoAwesome as AutoAwesomeIcon,
  RocketLaunch as RocketLaunchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../utils/api';
import { getCardBorderColor, getStationColor } from '../utils/locationColors';

const ICON_MAP = {
  Star: StarIcon,
  Favorite: FavoriteIcon,
  Home: HomeIcon,
  Flag: FlagIcon,
  Bolt: BoltIcon,
  Diamond: DiamondIcon,
  EmojiEvents: EmojiEventsIcon,
  LocalFireDepartment: LocalFireDepartmentIcon,
  WbSunny: WbSunnyIcon,
  Park: ParkIcon,
  AutoAwesome: AutoAwesomeIcon,
  RocketLaunch: RocketLaunchIcon,
};

function SimpleBillboard() {
  const theme = useTheme();
  const [activeNotifications, setActiveNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [globalBillboard, setGlobalBillboard] = useState(null);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [previousNotificationCount, setPreviousNotificationCount] = useState(0);
  
  // Audio reference for sound notifications
  const audioRef = useRef(null);
  const [audioLoaded, setAudioLoaded] = useState(false);

  // Fetch global billboard state
  const fetchGlobalBillboard = useCallback(async () => {
    try {
      const response = await api.get('/global-billboard');
      setGlobalBillboard(response.data);
    } catch (error) {
      console.error('Error fetching global billboard:', error);
      setError('Failed to fetch billboard data');
    }
  }, []);

  // Fetch active notifications
  const fetchActiveNotifications = useCallback(async (billboardData = null) => {
    try {
      const currentBillboard = billboardData || globalBillboard;
      if (currentBillboard?.activeBillboard) {
        const response = await api.get('/active-notifications', {
          params: {
            eventId: currentBillboard.activeBillboard.eventId,
            eventDate: currentBillboard.activeBillboard.eventDate
          }
        });
        setActiveNotifications(response.data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to fetch notifications');
    } finally {
      setIsLoading(false);
    }
  }, [globalBillboard]);

  // Load sound preference from localStorage
  useEffect(() => {
    const savedSoundPreference = localStorage.getItem('billboardSoundEnabled');
    if (savedSoundPreference !== null) {
      setSoundEnabled(JSON.parse(savedSoundPreference));
    }
  }, []);

  // Load audio file
  useEffect(() => {
    // Create a chime sound using Web Audio API instead of external file
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Store the audio context for later use
      audioRef.current = {
        context: audioContext
      };
      
      setAudioLoaded(true);
    } catch (error) {
      console.warn('Web Audio API not supported, sound notifications disabled:', error);
      setAudioLoaded(false);
    }
    
    // Cleanup
    return () => {
      if (audioRef.current && audioRef.current.context) {
        audioRef.current.context.close();
        audioRef.current = null;
      }
    };
  }, []);

  // Save sound preference to localStorage
  const handleSoundToggle = () => {
    const newSoundState = !soundEnabled;
    setSoundEnabled(newSoundState);
    localStorage.setItem('billboardSoundEnabled', JSON.stringify(newSoundState));
  };

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    console.log('🔊 playNotificationSound called:', { soundEnabled, audioLoaded, hasAudioRef: !!audioRef.current });
    
    if (soundEnabled && audioLoaded && audioRef.current) {
      try {
        const { context } = audioRef.current;
        console.log('🔊 Audio context state:', context.state);
        
        // Resume context if suspended (required by modern browsers)
        if (context.state === 'suspended') {
          console.log('🔊 Resuming suspended audio context...');
          context.resume().then(() => {
            console.log('🔊 Audio context resumed successfully');
            // Play sound after context is resumed
            playSoundAfterResume(context);
          }).catch(error => {
            console.error('🔊 Failed to resume audio context:', error);
          });
        } else {
          // Context is already running, play sound immediately
          playSoundAfterResume(context);
        }
        
      } catch (error) {
        console.warn('🔊 Error in playNotificationSound:', error);
      }
    } else {
      console.log('🔊 Sound not played - conditions not met:', { soundEnabled, audioLoaded, hasAudioRef: !!audioRef.current });
    }
  }, [soundEnabled, audioLoaded]);

  // Helper function to play sound after context is ready
  const playSoundAfterResume = (context) => {
    try {
      console.log('🔊 Playing chime sound...');
      
      // Create new oscillator for each play (can't reuse)
      const newOscillator = context.createOscillator();
      const newGainNode = context.createGain();
      
      newOscillator.connect(newGainNode);
      newGainNode.connect(context.destination);
      
      // Set up the chime sound
      newOscillator.type = 'sine';
      newOscillator.frequency.setValueAtTime(800, context.currentTime);
      newOscillator.frequency.setValueAtTime(1000, context.currentTime + 0.1);
      newOscillator.frequency.setValueAtTime(600, context.currentTime + 0.2);
      
      newGainNode.gain.setValueAtTime(0.3, context.currentTime);
      newGainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + 0.5);
      
      // Play the sound
      newOscillator.start(context.currentTime);
      newOscillator.stop(context.currentTime + 0.5);
      
      console.log('🔊 Chime sound started successfully');
      
    } catch (error) {
      console.error('🔊 Error playing sound after resume:', error);
    }
  };

  // Check for new notifications and play sound
  useEffect(() => {
    console.log('🔊 Notification count changed:', { 
      current: activeNotifications.length, 
      previous: previousNotificationCount,
      shouldPlay: activeNotifications.length > previousNotificationCount && previousNotificationCount > 0
    });
    
    if (activeNotifications.length > previousNotificationCount && previousNotificationCount > 0) {
      // New notifications were added
      console.log('🔊 New notifications detected, playing sound...');
      playNotificationSound();
    }
    setPreviousNotificationCount(activeNotifications.length);
  }, [activeNotifications.length, previousNotificationCount, playNotificationSound]);

  // Fetch data on mount and set up polling
  useEffect(() => {
    // Initial data fetch
    const initialFetch = async () => {
      await fetchGlobalBillboard();
      // Wait a bit for globalBillboard to be set, then fetch notifications
      setTimeout(() => {
        fetchActiveNotifications();
      }, 100);
    };
    
    initialFetch();
    
    // Set up polling interval
    const interval = setInterval(async () => {
      const billboardResponse = await api.get('/global-billboard');
      setGlobalBillboard(billboardResponse.data);
      
      // Fetch notifications with the fresh billboard data
      if (billboardResponse.data?.activeBillboard) {
        try {
          const notificationsResponse = await api.get('/active-notifications', {
            params: {
              eventId: billboardResponse.data.activeBillboard.eventId,
              eventDate: billboardResponse.data.activeBillboard.eventDate
            }
          });
          setActiveNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications in interval:', error);
        }
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, []); // Empty dependency array - only run on mount

  // Handle notifications when global billboard changes (but avoid infinite loops)
  useEffect(() => {
    if (globalBillboard?.activeBillboard) {
      fetchActiveNotifications(globalBillboard);
    }
  }, [globalBillboard?.activeBillboard?.eventId, globalBillboard?.activeBillboard?.eventDate]); // Only depend on the actual data, not the whole object

  // Get child emoji based on name - fun, cute, and child-friendly
  const getChildEmoji = (childName) => {
    const emojis = ['🦄', '🐻', '🐼', '🦊', '🐨', '🐸', '🦁', '🐯', '🐰', '🐶', '🐱', '🐵', '🦉', '🐙', '🦋', '🐝'];
    const index = childName.length % emojis.length;
    return emojis[index];
  };

  // Format time for display
  const formatTime = (date) => {
    if (!date) return '';
    try {
      return new Date(date).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      px: 2
    }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={64} sx={{ mb: 3 }} />
              <Typography variant="h4" color="text.secondary" sx={{ fontWeight: 600 }}>
                Loading pickup requests...
              </Typography>
            </Box>
          </Box>
        )}

        {/* No Active Billboard Message */}
        {!isLoading && !globalBillboard?.activeBillboard && (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: 'background.paper',
            borderRadius: 2
          }}>
            <Typography variant="h4" color="text.secondary">
              No active billboard
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              An admin needs to set up an active event and billboard first.
            </Typography>
          </Box>
        )}

        {/* Active Billboard Display */}
        {!isLoading && globalBillboard?.activeBillboard && (
          <Box>
            {/* Event Header */}
            <Box sx={{ 
              textAlign: 'center', 
              mb: 4,
              backgroundColor: 'primary.main',
              color: 'white',
              py: 3,
              borderRadius: 2,
              boxShadow: 3,
              position: 'relative'
            }}>
              {/* Sound Controls */}
              <Box sx={{ 
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                gap: 1
              }}>
                {/* Test Sound Button */}
                <Tooltip title="Test sound notification">
                  <IconButton
                    onClick={() => {
                      console.log('🔊 Test sound button clicked');
                      playNotificationSound();
                    }}
                    color="secondary"
                    sx={{
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'secondary.dark',
                      }
                    }}
                  >
                    🔊
                  </IconButton>
                </Tooltip>
                
                {/* Sound Toggle Button */}
                <Tooltip title={soundEnabled ? "Turn off sound notifications" : "Turn on sound notifications"}>
                  <IconButton
                    onClick={handleSoundToggle}
                    color={soundEnabled ? "primary" : "default"}
                    sx={{
                      backgroundColor: soundEnabled ? 'primary.main' : 'grey.300',
                      color: soundEnabled ? 'white' : 'grey.600',
                      '&:hover': {
                        backgroundColor: soundEnabled ? 'primary.dark' : 'grey.400',
                      }
                    }}
                  >
                    {soundEnabled ? <VolumeUp /> : <VolumeOff />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {globalBillboard.activeBillboard.eventName}
              </Typography>
              {globalBillboard.activeBillboard.eventDate && (
                <Typography variant="h5" sx={{ opacity: 0.9 }}>
                  {(() => {
                    const eventDate = globalBillboard.activeBillboard.eventDate;
                    console.log('📅 Billboard event date:', eventDate);
                    
                    try {
                      // Handle different date formats
                      let dateObj;
                      if (typeof eventDate === 'string') {
                        // If it's a YYYY-MM-DD string, parse it in UTC to avoid timezone issues
                        if (/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) {
                          const [year, month, day] = eventDate.split('-').map(num => parseInt(num, 10));
                          dateObj = new Date(Date.UTC(year, month - 1, day));
                        } else {
                          // For other date strings, parse normally
                          dateObj = new Date(eventDate);
                        }
                      } else if (eventDate instanceof Date) {
                        // If it's already a Date object
                        dateObj = eventDate;
                      } else {
                        console.warn('📅 Unknown date format:', eventDate);
                        return 'Date not available';
                      }
                      
                      // Check if date is valid
                      if (isNaN(dateObj.getTime())) {
                        console.warn('📅 Invalid date:', eventDate);
                        return 'Invalid date';
                      }
                      
                      console.log('📅 Parsed date object:', dateObj);
                      
                      // Format the date with UTC timezone to avoid timezone shifts
                      const formattedDate = dateObj.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        timeZone: 'UTC'
                      });
                      
                      console.log('📅 Formatted date:', formattedDate);
                      return formattedDate;
                      
                    } catch (error) {
                      console.error('📅 Error formatting date:', error);
                      return 'Date formatting error';
                    }
                  })()}
                </Typography>
              )}
            </Box>

            {/* Notifications Display */}
            {activeNotifications.length === 0 ? (
              <Box sx={{
                textAlign: 'center',
                py: 6,
                backgroundColor: 'background.paper',
                borderRadius: 2
              }}>
                <Typography variant="h5" color="text.secondary">
                  No children waiting for pickup
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Children will appear here when their security code is entered.
                </Typography>
              </Box>
            ) : (
              <Grid 
                container 
                spacing={2}
                sx={{
                  // Three-column layout optimized for landscape iPad
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 2
                }}
              >
                {activeNotifications.map((notification) => {
                  const borderColor = getCardBorderColor(
                    notification,
                    globalBillboard?.activeBillboard?.locationColors
                  );
                  const stationColor = getStationColor(
                    notification,
                    globalBillboard?.activeBillboard?.stationColors
                  );
                  return (
                    <Card
                      key={notification.id}
                      sx={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f4fafb 100%)',
                        color: 'text.primary',
                        border: `4px solid ${borderColor}`,
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
                        }
                      }}
                    >
                      <CardContent sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        '&:last-child': { pb: 2 }
                      }}>
                        {/* Child Name */}
                        <Typography variant="h4" sx={{
                          fontWeight: 900,
                          color: 'text.primary',
                          textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
                          letterSpacing: '1px',
                          fontSize: '2rem',
                          lineHeight: 1.2,
                          mb: 1
                        }}>
                          <span style={{ fontSize: '2.5rem' }}>{getChildEmoji(notification.childName)}</span> {notification.childName}
                        </Typography>
                        
                        {/* Security Code */}
                        <Typography variant="h5" sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          textShadow: '0 0 12px rgba(61,138,153,0.4)',
                          fontSize: '1.5rem',
                          mb: 1
                        }}>
                          {notification.securityCode}
                        </Typography>
                        
                        {/* Time and Location */}
                        <Typography variant="body1" sx={{
                          color: 'text.secondary',
                          fontSize: '0.95rem',
                          mb: notification.stationName ? 1 : 0
                        }}>
                          {formatTime(notification.notifiedAt)} • {notification.locationName}
                        </Typography>
                        
                        {/* Check-in Station badge: colored with optional icon + name */}
                        {(notification.stationName || notification.locationName) && (() => {
                          const StationIcon = notification.stationIcon ? ICON_MAP[notification.stationIcon] : null;
                          return (
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                backgroundColor: stationColor,
                                color: 'white',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                              }}
                            >
                              {StationIcon && <StationIcon sx={{ fontSize: '1rem' }} />}
                              {notification.stationName || notification.locationName}
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default SimpleBillboard; 